import { SITE } from '@/app/constants';
import type { AgentSettings } from '@/app/types';

export const MAX_VOLUME = 3;
export const MIN_VOLUME = 0;
export const VOLUME_STEP = 0.05;

/** 正式上线时间 */
export const OFFICIAL_LAUNCH_TIME = '2025-12-30T12:00:00Z'; // 北京时间 2025-12-30 20:00:00

/** 画布缩放 */
export const CANVAS_ZOOM_STEP = 0.1; // 每次缩放的步长
export const CANVAS_MIN_ZOOM = 0.1; // 最小缩放比例 10%
export const CANVAS_MAX_ZOOM = 2; // 最大缩放比例 200%

/** Canvas 焦点定位偏移量 */
export const CANVAS_FOCUS_OFFSET_X = 100;
export const CANVAS_FOCUS_OFFSET_Y = 100;

/** 分辨率选项配置 */
export const RESOLUTION_OPTIONS: Array<{
  label: string;
  value: NonNullable<AgentSettings['resolution']>;
}> = [
  { label: '720p', value: 'Resolution_720p' },
  { label: '1080p', value: 'Resolution_1080p' },
  { label: '2K', value: 'Resolution_2k' },
];

/** 宽高比选项配置 */
export const ASPECT_RATIO_OPTIONS: Array<{
  label: string;
  value: NonNullable<AgentSettings['ratio']>;
}> = [
  { label: '9:16', value: 'Ratio_9_16' },
  { label: '3:4', value: 'Ratio_3_4' },
  { label: '1:1', value: 'Ratio_1_1' },
  { label: '16:9', value: 'Ratio_16_9' },
  { label: '4:3', value: 'Ratio_4_3' },
];

export const VOICE_AUTO_IMAGE =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/voice-auto-eaaed95f.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/voice-auto-eaaed95f.webp';

export const MINI_VIDEO_DURATION_IN_SECONDS = 3;
export const MAX_VIDEO_DURATION_IN_SECONDS = 60;
export const MAX_VIDEO_FILE_SIZE = 500 * 1024 * 1024; // 500MB

export const DEMO_INPUT_AND_OUTPUT =
  SITE === 'china'
    ? [
        {
          title: '饮品展示',
          description: '根据这样的饮品生成一段广告视频',
          input: {
            images: [
              {
                id: 'project-demo1-1',
                url: 'https://image01-aliyun.vidu.zone/vidu/media-asset/project-demo1-1-39363712.webp',
              },
              {
                id: 'project-demo1-2',
                url: 'https://image01-aliyun.vidu.zone/vidu/media-asset/project-demo1-2-c0e1c3e9.webp',
              },
            ],
            text: '根据这样的饮品生成一段广告视频',
          },
          bg: 'https://image01-aliyun.vidu.zone/vidu/media-asset/placeholder-demo1-6a59cb9f.webp',
        },
        {
          title: '家电展示',
          description: '以该产品为主体，生成一条视频广告',
          input: {
            images: [
              {
                id: 'project-demo2',
                url: 'https://image01-aliyun.vidu.zone/vidu/media-asset/project-demo2-fd1ac0c0.webp',
              },
            ],
            text: '以该产品为主体，生成一条视频广告',
          },
          bg: 'https://image01-aliyun.vidu.zone/vidu/media-asset/placeholder-demo2-fdf5faf8.webp',
        },
        {
          title: '服饰展示',
          description: '生成一支广告片',
          input: {
            images: [
              {
                id: 'project-demo3-1',
                url: 'https://image01-aliyun.vidu.zone/vidu/media-asset/project-demo3-1-45838442.webp',
              },
              {
                id: 'project-demo3-2',
                url: 'https://image01-aliyun.vidu.zone/vidu/media-asset/project-demo3-2-d7600861.webp',
              },
            ],
            text: '生成一支广告片',
          },
          bg: 'https://image01-aliyun.vidu.zone/vidu/media-asset/placeholder-demo3-19a32ad9.webp',
        },
        {
          title: '数码产品展示',
          description: '以本产品为主体，生成一段广告视频',
          input: {
            images: [
              {
                id: 'project-demo4',
                url: 'https://image01-aliyun.vidu.zone/vidu/media-asset/project-demo4-babb544a.webp',
              },
            ],
            text: '以本产品为主体，生成一段广告视频',
          },
          bg: 'https://image01-aliyun.vidu.zone/vidu/media-asset/placeholder-demo4-ac2526c6.webp',
        },
      ]
    : [
        {
          title: 'Beverage display',
          description: 'Generate an advertising video based on this beverage.',
          input: {
            images: [
              {
                id: 'project-demo1-1',
                url: 'https://image01.cf.vidu.studio/vidu/media-asset/project-demo1-1-39363712.webp',
              },
              {
                id: 'project-demo1-2',
                url: 'https://image01.cf.vidu.studio/vidu/media-asset/project-demo1-2-c0e1c3e9.webp',
              },
            ],
            text: 'Generate an advertising video based on this beverage.',
          },
          bg: 'https://image01.cf.vidu.studio/vidu/media-asset/placeholder-demo1-6a59cb9f.webp',
        },
        {
          title: 'Home appliance display',
          description: 'Generate a video advertisement based on this product.',
          input: {
            images: [
              {
                id: 'project-demo2',
                url: 'https://image01.cf.vidu.studio/vidu/media-asset/project-demo2-fd1ac0c0.webp',
              },
            ],
            text: 'Generate a video advertisement based on this product.',
          },
          bg: 'https://image01.cf.vidu.studio/vidu/media-asset/placeholder-demo2-fdf5faf8.webp',
        },
        {
          title: 'Clothing Showcase',
          description: 'Generate an advertisement',
          input: {
            images: [
              {
                id: 'project-demo3-1',
                url: 'https://image01.cf.vidu.studio/vidu/media-asset/project-demo3-1-45838442.webp',
              },
              {
                id: 'project-demo3-2',
                url: 'https://image01.cf.vidu.studio/vidu/media-asset/project-demo3-2-d7600861.webp',
              },
            ],
            text: 'Generate an advertisement',
          },
          bg: 'https://image01.cf.vidu.studio/vidu/media-asset/placeholder-demo3-19a32ad9.webp',
        },
        {
          title: 'Digital product display',
          description: 'Generate an advertising video based on this product.',
          input: {
            images: [
              {
                id: 'project-demo4',
                url: 'https://image01.cf.vidu.studio/vidu/media-asset/project-demo4-babb544a.webp',
              },
            ],
            text: 'Generate an advertising video based on this product.',
          },
          bg: 'https://image01.cf.vidu.studio/vidu/media-asset/placeholder-demo4-ac2526c6.webp',
        },
      ];

export const PAGE_SIZE = 20;

export const DEFAULT_MODEL_AVATAR =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/default-model-2da8df58.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/default-model-2da8df58.webp';

export const COST_FETCH_INTERVAL = 1000 * 60 * 10; // 10分钟
